"use client";

import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { CarouselApi } from "@/components/ui/carousel";

const AUTOPLAY_DELAY = 5000;

type CarouselControlsProps = {
  api: CarouselApi | undefined;
  slidesCount?: number;
};

export const CarouselControls = ({
  api,
  slidesCount = 2,
}: CarouselControlsProps) => {
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(slidesCount);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number>(Date.now());
  const pausedProgressRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setProgress(0);
      pausedProgressRef.current = 0;
      startTimeRef.current = Date.now();
    };

    api.on("select", handleSelect);

    return () => {
      api.off("select", handleSelect);
    };
  }, [api]);

  const scrollToNext = useCallback(() => {
    if (!api) return;

    if (api.canScrollNext()) {
      api.scrollNext();
    } else {
      api.scrollTo(0);
    }
  }, [api]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!(isPlaying && api)) {
      return;
    }

    setProgress(pausedProgressRef.current);
    startTimeRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const calculatedProgress =
        pausedProgressRef.current + (elapsed / AUTOPLAY_DELAY) * 100;

      if (calculatedProgress >= 100) {
        pausedProgressRef.current = 0;
        scrollToNext();
      } else {
        setProgress(calculatedProgress);
      }
    }, 50);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, api, scrollToNext, current]);

  const handleTogglePlayPause = () => {
    if (isPlaying) {
      pausedProgressRef.current = progress;
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (index: number) => {
    if (!api) return;

    api.scrollTo(index);
    setProgress(0);
    pausedProgressRef.current = 0;
    startTimeRef.current = Date.now();
  };

  const handleProgressKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleProgressClick(index);
    }
  };

  if (slidesCount <= 1) {
    return null;
  }

  return (
    <div className="mx-auto flex w-full items-center justify-center gap-3">
      <Button
        aria-label={isPlaying ? "Pause autoplay" : "Play autoplay"}
        className="h-8 w-8 shrink-0 border-0"
        onClick={handleTogglePlayPause}
        size="icon"
        variant="outline"
      >
        {isPlaying ? (
          <IconPlayerPauseFilled className="h-6 w-6" />
        ) : (
          <IconPlayerPlayFilled className="h-6 w-6" />
        )}
      </Button>

      <div className="flex w-36 gap-2">
        {Array.from({ length: count }).map((_, index) => (
          <Button
            aria-current={index === current ? "true" : "false"}
            aria-label={`Go to slide ${index + 1}`}
            className="relative h-2.5 min-h-0 flex-1 cursor-pointer justify-start overflow-hidden rounded-full bg-brand-grey1 p-0.5 transition-opacity hover:bg-brand-grey1 focus:outline-none"
            key={index.toString()}
            onClick={() => handleProgressClick(index)}
            onKeyDown={(event) => handleProgressKeyDown(event, index)}
            tabIndex={0}
            type="button"
          >
            {index === current && (
              <div
                className="h-full rounded-full bg-brand-green2-300 transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            )}
          </Button>
        ))}
      </div>
    </div>
  );
};
